import { Component, OnInit, Inject, OnDestroy } from '@angular/core';

import { translate } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { RGBAColor } from '@inmoment/the-kitchen/src/lib/Pickers/ColorPicker/ColorPicker.model';

import { WidgetGroup } from 'app/providers/bridge-api/dashboard.service';
import { MatDialogRef, MAT_DIALOG_DATA } from 'app/services/dialog.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { DEFAULT_PROJECT_COLOR_PALETTE } from '../../../../providers/storage-api/project.service';
import { generalNameValidator } from '../../../../utils/customValidators';
import { Utils } from '../../../../utils/utils';
import { ChartCustomColorService } from '../../services/chart-custom-color.service';
import { DEFAULT_COMPARISON_COLOR } from '../../services/comparison-handler-service';

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-group-create',
  templateUrl: './widget-group-create.component.html',
  styleUrls: ['./widget-group-create.component.scss'],
  standalone: false,
})
export class WidgetGroupCreateDialogComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  public name = '';
  public description: string;
  public isLoading = { widgetGroupList: false, confirm: false };
  public widgetGroupList: WidgetGroup[];
  public isVisible = false;
  public widgetGroupNameStatus: ErrorObject = { state: EnumErrorStates.SUCCESS, text: '' };

  isRename = false;
  widgetGroupColor: RGBAColor = DEFAULT_COMPARISON_COLOR;
  projectPalette: { color: RGBAColor }[] = DEFAULT_PROJECT_COLOR_PALETTE.map((colorString) => ({
    color: ChartCustomColorService.colorStringToRgbaColor(colorString),
  }));
  widgetGroupColorChange$ = new Subject<RGBAColor>();
  destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<WidgetGroupCreateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.refresh();
    if (data.name) {
      this.name = data.name;
      this.isRename = true;
    }
    if (data.project?.colorPalette) {
      this.projectPalette = data.project.colorPalette.map((colorString) => ({
        color: ChartCustomColorService.colorStringToRgbaColor(colorString),
      }));
    }
  }

  ngOnInit() {
    this.isVisible = true;
    this.widgetGroupColor = this.data?.widgetGroupColor ?? DEFAULT_COMPARISON_COLOR;
    this.widgetGroupColorChange$
      .pipe(
        debounceTime(100),
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => Utils.deepEquals(prev, curr))
      )
      .subscribe((value) => (this.widgetGroupColor = value));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public async refresh(): Promise<void> {
    if (!this.widgetGroupList) {
      this.isLoading.widgetGroupList = true;
      this.widgetGroupList = this.data.widgetGroups || [];
      this.isLoading.widgetGroupList = false;
    }
  }

  public onValidateWidgetGroupName() {
    generalNameValidator(
      this.name,
      this.widgetGroupNameStatus,
      translate('widget-group-create-component.widget-group'),
      24,
      true,
      this.widgetGroupList.map((group) => group.title),
      false
    );
  }

  public cancel(): void {
    this.isVisible = false;
    this.dialogRef.close(false);
  }

  public canConfirm(): boolean {
    if (
      !this.name.length ||
      this.name.trim().length < 1 ||
      this.widgetGroupNameStatus.state !== EnumErrorStates.SUCCESS
    ) {
      return false;
    }

    if (this.isLoading.confirm) {
      return false;
    }

    return true;
  }

  public async confirm(): Promise<void> {
    this.isLoading.confirm = true;
    this.dialogRef.close({
      name: this.name,
      widgetGroupColor: this.widgetGroupColor,
    });
  }
}
